import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import AirportWeatherReadout from 'components/airspace/AirportWeatherReadout';

import buildCardContent from './cardContent';
import buildDetailRows from './cardDetails';
import CeilingRow from './ceilingRow';
import { TEST_ID } from './constants';
import DetailsSection from './detailsSection';
import LaancIcon from './laancIcon';
import TitleRow from './titleRow';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const SEVERITY_CLASS = {
    advisory: styles.severityAdvisory,
    critical: styles.severityCritical,
    info: styles.severityInfo,
    prohibited: styles.severityProhibited,
    safe: styles.severitySafe,
    warning: styles.severityWarning
};

const AirspaceCard: FunctionComponent<Props> = ({
    className = '',
    isExpanded = false,
    onToggle,
    restriction,
    testId = TEST_ID,
    weather = null
}) => {
    const content = buildCardContent(restriction);
    const severityClass = SEVERITY_CLASS[restriction.severity];
    const detailRows = buildDetailRows(restriction);
    const hasWeather = weather?.droneWeather !== null && weather?.droneWeather !== undefined;
    const canExpand = (detailRows.length > 0 || hasWeather) && onToggle !== undefined;
    const interactiveClass = canExpand ? styles.interactive : '';
    const expandedClass = isExpanded ? styles.expanded : '';

    return (
        <HorizontalContainer
            className={`${styles.card} ${severityClass} ${interactiveClass} ${expandedClass} ${className}`}
            onClick={canExpand ? onToggle : undefined}
            testId={testId}>
            <Wrapper className={styles.severityStripe} />

            <VerticalContainer className={styles.content}>
                <HorizontalContainer className={styles.titleRow}>
                    <VerticalContainer className={styles.titleGroup}>
                        {content.categoryLabel === null ? null : (
                            <Wrapper className={styles.categoryLabel}>
                                {content.categoryLabel}
                            </Wrapper>
                        )}

                        <TitleRow content={content} />
                    </VerticalContainer>

                    {content.showLaancIcon ? (
                        <Wrapper className={styles.laancIconSlot}>
                            <LaancIcon />
                        </Wrapper>
                    ) : null}
                </HorizontalContainer>

                {content.distanceText === null ? null : (
                    <Wrapper className={styles.distance}>{content.distanceText}</Wrapper>
                )}

                {content.subtitle === null ? null : (
                    <Wrapper className={styles.subtitle}>{content.subtitle}</Wrapper>
                )}

                <CeilingRow content={content} />

                {content.body === null ? null : (
                    <Wrapper className={styles.body}>{content.body}</Wrapper>
                )}

                {hasWeather && weather !== null ? (
                    <Wrapper className={styles.weatherSection}>
                        <AirportWeatherReadout
                            isExpanded={isExpanded}
                            showStation={false}
                            weather={weather}
                        />
                    </Wrapper>
                ) : null}

                {isExpanded ? <DetailsSection rows={detailRows} /> : null}
            </VerticalContainer>
        </HorizontalContainer>
    );
};

export default AirspaceCard;
